import { BIRDModelController } from '@sas-dvr/ltjs-bird';
import { ClosetObject, EventDispatcher, PortableEvent } from '@sas-dvr/ltjs-commons';
import { ofType } from 'redux-observable';
import { MonoTypeOperatorFunction, Observable, using, throwError, fromEventPattern } from 'rxjs';
import { filter, takeUntil, map } from 'rxjs/operators';
import { filterNulls } from './arrayUtils';
import { AnyAction } from 'redux';
import { EpicActions } from '../actions/epicActions';

/**
 * Simplifies redux-observable's ofType operator's type to take a single generic.
 */
export function ofActionCreatorType<ActionCreator extends (...args: any[]) => AnyAction>(
  ...types: [ReturnType<ActionCreator>['type'], ...ReturnType<ActionCreator>['type'][]]
) {
  return ofType<AnyAction, typeof types, ReturnType<ActionCreator>>(...types);
}

export function fromPortableEvent<T extends PortableEvent = PortableEvent>(
  dispatcher: EventDispatcher,
  type: number
): Observable<T> {
  return fromEventPattern(
    (handler) => dispatcher.attachEvent(type, handler),
    (_handler, handle: ReturnType<typeof dispatcher.attachEvent>) => handle.detach()
  );
}

/**
 * Creates a pass-through operator function that manages the lifetime of the given ClosetObject.
 *
 * The resulting Observable must receive a subscription immediately, or the ClosetObject will be allowed to expire.
 *
 * @param wrapper The ClosetObject to retain
 */
export function withRetained<T>(wrapper: ClosetObject): MonoTypeOperatorFunction<T> {
  return (source) => {
    return using(
      () => {
        if (wrapper.isExpired()) {
          return;
        }
        wrapper.retain();
        return { unsubscribe: () => wrapper.release() };
      },
      (resource) => {
        return resource
          ? source
          : throwError(new Error('Cannot create a new subscription after the wrapper has expired'));
      }
    );
  };
}

/**
 * Creates a pass-through operator function that manages the lifetime of an array of ClosetObjects.
 *
 * The resulting Observable must receive a subscription immediately, or the ClosetObjects will be allowed to expire.
 *
 * @param wrappers The ClosetObjects to retain
 */
export function withRetainedArray<T>(
  wrappers: (ClosetObject | null)[] | null
): MonoTypeOperatorFunction<T> {
  const objects = filterNulls(wrappers);

  return (source) => {
    return using(
      () => {
        if (objects.some((object) => object.isExpired())) {
          return;
        }
        objects.forEach((o) => o.retain());
        return { unsubscribe: () => objects.forEach((o) => o.release()) };
      },
      (resource) => {
        return resource
          ? source
          : throwError(
              new Error('Cannot create a new subscription after the wrappers have expired')
            );
      }
    );
  };
}

export function takeUntilUnloaded<T>(
  controller: BIRDModelController,
  actions$: Observable<AnyAction>
): MonoTypeOperatorFunction<T> {
  return takeUntil(fromUnload(controller, actions$));
}

export function fromUnload<T extends BIRDModelController>(
  controller: T,
  actions$: Observable<AnyAction>
): Observable<T> {
  return actions$.pipe(
    ofActionCreatorType<typeof EpicActions.actions.unloadBirdModel>(
      EpicActions.actionTypes.UnloadBirdModel
    ),
    map((value) => value.payload),
    filter<BIRDModelController, T>(
      (unloadedController): unloadedController is T => unloadedController === controller
    )
  );
}

export function notNull<T>() {
  return filter<T, Exclude<T, null>>((value): value is Exclude<T, null> => value !== null);
}

export function filterPayloadId<T extends { payload: { id: string } }>(id: string) {
  return filter<T>((action) => action.payload.id === id);
}
